import Vue from 'vue'
import Router from 'vue-router'
import {
  collectValidSubPaths,
  firstPathSegment,
  getSubPath,
  isStoredAuthToken,
  loginRedirect,
  safeCountryPrefix,
  withCurrentQueryAndHash
} from './routeGuardHelpers'
import {
  safeGetStorageItem,
  safeLocalStorage,
  safeRemoveStorageItem,
  safeSetStorageItem
} from '@/utils/safeStorage'
import { SINGLE_COUNTRY } from '@/config/singleCountry'
Vue.use(Router)

// 单国实例 SINGLE_COUNTRY 抽到 @/config/singleCountry(router + TopNav 等共用);多国扩展置 null 即恢复。
function applySingleCountry(storage) {
  if (!SINGLE_COUNTRY || !storage) return
  // 幂等校正:被 home/TopNav 污染进来的脏 currency/platform 每次导航都纠回基准值。
  // 例外:defaultLanguage 是用户偏好 —— 用户在「设置」里主动切过语言后(langUserSet=1)就不再纠回,
  // 实现「默认英语 + 可切换持久」;未切过的访客仍纠成基准语言(en)。
  const langUserSet = safeGetStorageItem(storage, 'langUserSet') === '1'
  Object.keys(SINGLE_COUNTRY).forEach(k => {
    if (k === 'defaultLanguage' && langUserSet) return
    if (safeGetStorageItem(storage, k) !== String(SINGLE_COUNTRY[k])) {
      safeSetStorageItem(storage, k, String(SINGLE_COUNTRY[k]))
    }
  })
}

function browserLocation() {
  try {
    if (typeof window !== 'undefined' && window && window.location) {
      return window.location
    }
  } catch (e) {
    return null
  }
  return null
}

function browserLocationValue(key) {
  const location = browserLocation()
  if (!location) return ''
  try {
    return location[key] || ''
  } catch (e) {
    return ''
  }
}

function redirectWithLocation(path, next) {
  const location = browserLocation()
  if (location) {
    try {
      location.href = path
      return
    } catch (e) {
      // Fall back to router navigation when browser location assignment is blocked.
    }
  }
  next(path)
}

const router = new Router({
  mode: 'history',
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/:prefix?', // 可选动态前缀参数
      component: () => import('@/views/layout'),
      children: [
        {
          path: 'Crowdfunding',
          name: 'Crowdfunding',
          component: () => import('@/views/layout/Crowdfunding'),
          meta: { keepAlive: true }
        },
        {
          path: 'Country',
          name: 'Country',
          component: () => import('@/views/layout/Country'),
          meta: { keepAlive: true }
        },
        {
          path: 'home',
          name: 'Home',
          component: () => import('@/views/layout/home'),
          meta: { keepAlive: true }
        },
        {
          path: 'menu',
          name: 'Menu',
          component: () => import('@/views/layout/menu'),
          meta: { keepAlive: true }
        },
        {
          path: 'Support',
          name: 'Support',
          component: () => import('@/views/layout/Support'),
          meta: { keepAlive: true }
        },
        {
          path: 'bonus',
          name: 'Bonus',
          component: () => import('@/views/layout/bonus'),
          meta: { keepAlive: true }
        },
        {
          path: 'lottery',
          name: 'Lottery',
          component: () => import('@/views/layout/lottery'),
          meta: { keepAlive: true }
        },
        {
          path: 'share',
          name: 'Share',
          component: () => import('@/views/layout/share'),
          meta: { keepAlive: true }
        },
        {
          path: 'mine',
          name: 'Mine',
          component: () => import('@/views/layout/mine'),
          meta: { keepAlive: true }
        },
        {
          path: 'activity',
          name: 'Activity',
          component: () => import('@/views/layout/activity'),
          meta: { keepAlive: true }
        },
        {
          path: 'rechargeCont',
          name: 'RechargeCont',
          component: () => import('@/views/layout/rechargeCont')
        },
        {
          path: 'firstDepositGift',
          name: 'FirstDepositGift',
          component: () => import('@/views/details/activity/firstDepositGift')
        },
        {
          path: 'newbieGift',
          name: 'NewbieGift',
          component: () => import('@/views/details/activity/newbieGift')
        }
      ]
    },
    // 独立路由前缀支持
    {
      path: '/:prefix?/activityFistRecharge',
      name: 'ActivityFistRecharge',
      component: () => import('@/views/details/bonus/activityFistRecharge')
    },
    {
      path: '/:prefix?/activityRecharge',
      name: 'ActivityRecharge',
      component: () => import('@/views/details/bonus/activityRecharge')
    },
    {
      path: '/:prefix?/vipSignIn',
      name: 'VipSignIn',
      component: () => import('@/views/details/bonus/vipSignIn')
    },
    {
      path: '/:prefix?/activityCooperate',
      name: 'ActivityCooperate',
      component: () => import('@/views/details/bonus/activityCooperate')
    },
    {
      path: '/:prefix?/cashBack',
      name: 'CashBack',
      component: () => import('@/views/details/bonus/cashBack')
    },
    {
      path: '/:prefix?/teamRebates',
      name: 'TeamRebates',
      component: () => import('@/views/details/bonus/teamRebates')
    },
    {
      path: '/:prefix?/redPacket',
      name: 'RedPacket',
      component: () => import('@/views/details/bonus/redPacket')
    },
    {
      path: '/:prefix?/signIn',
      name: 'SignIn',
      component: () => import('@/views/details/bonus/signIn')
    },
    {
      path: '/:prefix?/dailyCheckIn',
      name: 'DailyCheckIn',
      component: () => import('@/views/details/activity/dailyCheckIn')
    },
    {
      path: '/:prefix?/rewardHistory',
      name: 'RewardHistory',
      component: () => import('@/views/details/activity/rewardHistory')
    },
    {
      path: '/:prefix?/seasonPass',
      name: 'SeasonPass',
      component: () => import('@/views/details/activity/seasonPass')
    },
    {
      path: '/:prefix?/seasonHistory',
      name: 'SeasonHistory',
      component: () => import('@/views/details/activity/seasonHistory')
    },
    {
      path: '/:prefix?/weeklyMissions',
      name: 'WeeklyMissions',
      component: () => import('@/views/details/activity/weeklyMissions')
    },
    {
      path: '/:prefix?/luckyReferral',
      name: 'LuckyReferral',
      component: () => import('@/views/details/activity/luckyReferral')
    },
    {
      path: '/:prefix?/referFriend',
      name: 'ReferFriend',
      component: () => import('@/views/details/bonus/referFriend')
    },
    {
      path: '/:prefix?/bankAdd',
      name: 'BankAdd',
      component: () => import('@/views/details/recharge/bankAdd')
    },
    {
      path: '/:prefix?/authName',
      name: 'AuthName',
      component: () => import('@/views/details/recharge/authName')
    },
    {
      path: '/:prefix?/setPassWord',
      name: 'SetPassWord',
      component: () => import('@/views/details/recharge/setPassWord')
    },
    {
      path: '/:prefix?/Alliance',
      name: 'Alliance',
      component: () => import('@/views/details/mine/Alliance')
    },
    {
      path: '/:prefix?/vip',
      name: 'Vip',
      component: () => import('@/views/details/mine/vip')
    },
    {
      path: '/:prefix?/profile',
      name: 'Profile',
      component: () => import('@/views/details/mine/profile')
    },
    {
      path: '/:prefix?/passwordLogin',
      name: 'PsswordLogin',
      component: () => import('@/views/details/mine/passwordLogin')
    },
    {
      path: '/:prefix?/bank',
      name: 'Bank',
      component: () => import('@/views/details/mine/bank')
    },
    {
      path: '/:prefix?/avatar',
      name: 'Avatar',
      component: () => import('@/views/details/mine/avatar')
    },
    {
      path: '/:prefix?/privacy',
      name: 'Privacy',
      component: () => import('@/views/details/mine/privacy')
    },
    {
      path: '/:prefix?/billGame',
      name: 'BillGame',
      component: () => import('@/views/details/mine/billGame')
    },
    {
      path: '/:prefix?/gameHall',
      name: 'GameHall',
      component: () => import('@/views/details/mine/gameHall')
    },
    {
      path: '/:prefix?/payOrder',
      name: 'PayOrder',
      component: () => import('@/views/details/mine/payOrder')
    },
    {
      path: '/:prefix?/joinUs',
      name: 'JoinUs',
      component: () => import('@/views/details/bonus/joinUs')
    },
    {
      path: '/:prefix?/recordOrder',
      name: 'RecordOrder',
      component: () => import('@/views/details/recharge/recordOrder')
    },
    {
      path: '/:prefix?/orderDetail',
      name: 'OrderDetail',
      component: () => import('@/views/details/recharge/orderDetail')
    },
    {
      path: '/:prefix?/firstRegister',
      name: 'FirstRegister',
      component: () => import('@/views/details/bonus/firstRegister')
    },
    {
      path: '/:prefix?/cp',
      name: 'Cp',
      component: () => import('@/views/details/mine/cp')
    },
    {
      path: '/:prefix?/email',
      name: 'Email',
      component: () => import('@/views/details/mine/email')
    },
    {
      path: '/:prefix?/myCollection',
      name: 'MyCollection',
      component: () => import('@/views/details/mine/myCollection')
    },
    {
      path: '/:prefix?/category',
      name: 'Category',
      component: () => import('@/views/details/home/category')
    },
    {
      path: '/:prefix?/messageCenter',
      name: 'MessageCenter',
      component: () => import('@/views/details/home/messageCenter')
    },
    {
      path: '/:prefix?/searchGame',
      name: 'SearchGame',
      component: () => import('@/views/details/home/searchGame')
    },
    {
      path: '/:prefix?/gameDetail',
      name: 'GameDetail',
      component: () => import('@/views/details/home/gameDetail')
    },
    {
      path: '/:prefix?/lotteryDetail',
      name: 'LotteryDetail',
      component: () => import('@/views/details/lottery/lotteryDetail')
    },
    {
      path: '/:prefix?/betHistory',
      name: 'BetHistory',
      component: () => import('@/views/details/lottery/betHistory')
    },
    {
      path: '/:prefix?/latestResults',
      name: 'LatestResults',
      component: () => import('@/views/details/lottery/latestResults')
    },
    {
      path: '/:prefix?/bettingDetails',
      name: 'BettingDetails',
      component: () => import('@/views/details/lottery/bettingDetails')
    },
    {
      path: '/:prefix?/settings',
      name: 'Settings',
      component: () => import('@/views/details/mine/settings')
    },
    {
      path: '/:prefix?/editProfile',
      name: 'EditProfile',
      component: () => import('@/views/details/mine/editProfile')
    },
    {
      path: '/:prefix?/vipLevels',
      name: 'VipLevels',
      component: () => import('@/views/details/mine/vipLevels')
    },
    {
      path: '/:prefix?/passwordSuccess',
      name: 'PasswordSuccess',
      component: () => import('@/views/details/mine/passwordSuccess')
    },
    {
      path: '/:prefix?/transactionRecords',
      name: 'TransactionRecords',
      component: () => import('@/views/details/mine/transactionRecords')
    },
    {
      path: '/:prefix?/phoneUnbound',
      name: 'PhoneUnbound',
      component: () => import('@/views/details/mine/phoneUnbound')
    },
    {
      path: '/:prefix?/googlePhoneBind',
      name: 'GooglePhoneBind',
      component: () => import('@/views/details/mine/googlePhoneBind')
    }
  ]
})

const validSubPaths = collectValidSubPaths(router.options.routes)
const googlePhoneBindRouteNames = new Set(['GooglePhoneBind'])
const googlePhoneBindPaths = new Set(['/googlePhoneBind'])
const hiddenH5Paths = new Set(['/lottery'])

const needAuthRouteNames = new Set([
  'Crowdfunding',
  'RechargeCont',
  'ActivityFistRecharge',
  'ActivityRecharge',
  'ActivityCooperate',
  'FirstDepositGift',
  'NewbieGift',
  'VipSignIn',
  'CashBack',
  'TeamRebates',
  'RedPacket',
  'SignIn',
  'DailyCheckIn',
  'RewardHistory',
  'SeasonPass',
  'SeasonHistory',
  'WeeklyMissions',
  'LuckyReferral',
  'ReferFriend',
  'Mine',
  'Share',
  'BankAdd',
  'AuthName',
  'SetPassWord',
  'Bank',
  'Avatar',
  'Profile',
  'Vip',
  'PsswordLogin',
  'BillGame',
  'GameHall',
  'LotteryDetail',
  'BetHistory',
  'LatestResults',
  'BettingDetails',
  'PayOrder',
  'RecordOrder',
  'OrderDetail',
  'Cp',
  'Email',
  'MyCollection',
  'Settings',
  'EditProfile',
  'VipLevels',
  'Alliance',
  'FirstRegister',
  'PasswordSuccess',
  'TransactionRecords',
  'PhoneUnbound'
])
const needAuthPaths = new Set([
  '/Crowdfunding',
  '/rechargeCont',
  '/activityFistRecharge',
  '/activityRecharge',
  '/activityCooperate',
  '/firstDepositGift',
  '/newbieGift',
  '/vipSignIn',
  '/cashBack',
  '/teamRebates',
  '/redPacket',
  '/signIn',
  '/dailyCheckIn',
  '/rewardHistory',
  '/seasonPass',
  '/seasonHistory',
  '/weeklyMissions',
  '/luckyReferral',
  '/referFriend',
  '/mine',
  '/share',
  '/bankAdd',
  '/authName',
  '/setPassWord',
  '/bank',
  '/avatar',
  '/profile',
  '/vip',
  '/passwordLogin',
  '/billGame',
  '/gameHall',
  '/lotteryDetail',
  '/betHistory',
  '/latestResults',
  '/bettingDetails',
  '/payOrder',
  '/recordOrder',
  '/orderDetail',
  '/cp',
  '/email',
  '/myCollection',
  '/settings',
  '/editProfile',
  '/vipLevels',
  '/Alliance',
  '/firstRegister',
  '/passwordSuccess',
  '/transactionRecords',
  '/phoneUnbound'
])

// 全局前置守卫 - 核心逻辑：
// 1. 强制同步URL前缀与本地country
// 2. / 跳 /Country，/xx 跳 /xx/home（并刷新页面）
router.beforeEach((to, from, next) => {
  // Google OAuth callback: save code before redirects strip it
  // Google OAuth callback: if URL has code param, let App.vue handle it — do NOT redirect
  const storage = safeLocalStorage()
  const urlSearch = new URLSearchParams(browserLocationValue('search'))
  const rawCode = to.query.code || urlSearch.get('code')
  const googleCallbackPath = '/api/auth/google/callback'
  const isGoogleCallbackPath = to.path === googleCallbackPath
  const staleGoogleCallbackParams = ['googleRedirectUri', 'redirectUri', 'redirect_uri'].filter(key => to.query && to.query[key])
  if (rawCode) {
    safeSetStorageItem(storage, 'googleAuthCode', rawCode)
    const queryRedirectUri = to.query && (to.query.googleRedirectUri || to.query.redirectUri || to.query.redirect_uri)
    if (queryRedirectUri) {
      safeSetStorageItem(storage, 'googleRedirectUri', queryRedirectUri)
    }
    const callbackOrigin = browserLocationValue('origin')
    if (callbackOrigin && isGoogleCallbackPath) {
      safeSetStorageItem(storage, 'googleRedirectUri', callbackOrigin + googleCallbackPath)
      const savedPrefix = safeCountryPrefix(safeGetStorageItem(storage, 'country'))
      const defaultPrefix = safeCountryPrefix(SINGLE_COUNTRY && SINGLE_COUNTRY.country)
      return redirectWithLocation(`/${savedPrefix || defaultPrefix || 'CIV'}/home`, next)
    }
    return next()
  }
  if (staleGoogleCallbackParams.length) {
    const query = { ...to.query }
    staleGoogleCallbackParams.forEach(key => {
      delete query[key]
    })
    return next({ path: to.path, query, hash: to.hash, replace: true })
  }

  // 单国实例:强制本国上下文,纠正任何残留的非本国 country/currency(历史多国/NG 残留)
  applySingleCountry(storage)

  const rawSavedCountry = safeGetStorageItem(storage, 'country')
  const savedCountry = safeCountryPrefix(rawSavedCountry) // 本地存储的前缀
  if (rawSavedCountry && rawSavedCountry !== savedCountry) {
    safeRemoveStorageItem(storage, 'country')
  }
  const routePrefix = safeCountryPrefix(to.params.prefix) // URL中的前缀
  let finalPrefix = ''
  const pathSegments = to.path.split('/').filter(Boolean)
  const firstSegment = firstPathSegment(to)
  const firstSegmentIsRoute = validSubPaths.has(firstSegment)

  // 1. 前缀同步逻辑（保持原有规则）
  if (savedCountry) {
    finalPrefix = savedCountry
    // 如果URL前缀和本地不一致，强制重定向到本地前缀的URL
    if (routePrefix !== savedCountry) {
      const pathWithoutPrefix = firstSegmentIsRoute
        ? to.path
        : to.path.replace(/^\/[^/]+/, '') || ''
      const correctPath = withCurrentQueryAndHash(`/${savedCountry}${pathWithoutPrefix}`, to)
      // Google OAuth callback: use full page reload so App.vue re-mounts and processes the code
      if (safeGetStorageItem(storage, 'googleAuthCode')) {
        return redirectWithLocation(correctPath, next)
      }
      return next(correctPath)
    }
  } else {
    // 本地无值：用URL前缀并存储到本地
    finalPrefix = routePrefix || ''
    if (finalPrefix) {
      safeSetStorageItem(storage, 'country', finalPrefix)
    }
  }

  // 2. 核心重定向规则：/ 跳 /Country，/xx 跳 /xx/home（带刷新）
  // 分割路径并过滤空值，得到有效路径段数组
  // 场景1：访问纯根路径 / → 跳 /Country
  if (pathSegments.length === 0) {
    // Google OAuth callback at root: need full reload for App.vue to process code
    if (safeGetStorageItem(storage, 'googleAuthCode') && savedCountry) {
      return redirectWithLocation(`/${savedCountry}/home`, next)
    }
    // 单国实例已强制 savedCountry,直接进首页,跳过选国家弹窗
    if (savedCountry) return next(`/${savedCountry}/home`)
    return next('/Country')
  }
  // 场景2：访问纯前缀路径 /xx（无任何子路径）→ 跳 /xx/home 并刷新页面
  else if (
    pathSegments.length === 1 &&
    !validSubPaths.has(pathSegments[0])
  ) {
    const prefix = safeCountryPrefix(pathSegments[0])
    if (!prefix) {
      return next(savedCountry ? `/${savedCountry}/home` : '/Country')
    }
    // 存储前缀到本地
    safeSetStorageItem(storage, 'country', prefix)
    const targetPath = withCurrentQueryAndHash(`/${prefix}/home`, to)
    return next(targetPath)
  }

  const currentPath = getSubPath(to, validSubPaths)

  if (hiddenH5Paths.has(currentPath)) {
    return next(finalPrefix ? `/${finalPrefix}/home` : '/home')
  }

  if (googlePhoneBindRouteNames.has(to.name) || googlePhoneBindPaths.has(currentPath)) {
    const googleThirdUuid = safeGetStorageItem(storage, 'googleThirdUuid')
    if (!googleThirdUuid) {
      return next(finalPrefix ? `/${finalPrefix}/home` : '/home')
    }
    return next()
  }

  // 3. 原有权限验证逻辑（保持不变）
  if (needAuthRouteNames.has(to.name) || needAuthPaths.has(currentPath)) {
    const token = safeGetStorageItem(storage, 'token')
    if (!isStoredAuthToken(token)) {
      if (!from.fullPath) {
        next(loginRedirect(finalPrefix, to))
      } else {
        setTimeout(() => {
          next(loginRedirect(finalPrefix, to))
        }, 0)
      }
      return
    }
  }

  // 合法路由直接放行
  next()
})

export default router
